import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { useEventLocationType } from "@circle-react/components/Events/EditForm/useEventLocationType";
import { LOCATION_TYPES } from "@circle-react/components/Events/helpers/constants";
import { CohostsObserver } from "@circle-react/components/LiveStreams/common/CohostsObserver";
import { INITIAL_HOST_NUMBER } from "@circle-react/helpers/liveStreamRoomHelpers";
import type { Event } from "@circle-react/types/Event";
import {
  CommunityMemberPicker,
  VARIANTS,
} from "@circle-react-shared/CommunityMemberPicker";
import { Typography } from "@circle-react-uikit/Typography";
import { FormItem } from "../../../Common";
import { useEventAttendeesApi } from "../../../hooks/useEventAttendeesApi";
import { ATTENDEE_ROLES } from "../../../utils";
import { AttendeesWarningMessage } from "../AttendeesWarningMessage";
import { List } from "../List";
import { Pagination } from "../Pagination";

interface CohostsProps {
  event: Event;
}

export const Cohosts = ({ event }: CohostsProps) => {
  const [isCohostLimitReached, setIsCohostLimitReached] = useState(false);
  const {
    records,
    refetch,
    fetchPrevPage,
    fetchNextPage,
    hasPrevPage,
    hasNextPage,
    totalRecordCount,
    totalConfirmedRecordCount,
    addAttendeesAsync,
    startRecordNumber,
    endRecordNumber,
  } = useEventAttendeesApi(event, ATTENDEE_ROLES.COHOST);

  const { resetField } = useFormContext();
  const onAddCohosts = async (cohostIds: string[]) => {
    if (!cohostIds?.length) {
      return;
    }
    try {
      await addAttendeesAsync({
        community_member_ids: cohostIds,
        role: ATTENDEE_ROLES.COHOST,
      });
      resetField("attendee_invite_attributes.invited_cohost_ids");
    } catch (err) {
      console.error(err);
    }
  };
  const { locationType } = useEventLocationType(event);
  const shouldDisableCommunityPickerSearch = isCohostLimitReached;
  const excludedMemberIds = records?.map(record => record.community_member_id);
  const isLiveLocation =
    locationType === LOCATION_TYPES.LIVE_STREAM ||
    locationType === LOCATION_TYPES.LIVE_ROOM;

  return (
    <div className="flex h-full flex-col items-stretch pt-4">
      <div className="flex w-full items-center justify-between">
        <Typography.LabelMd weight="semibold">
          {`${totalConfirmedRecordCount || 0} ${t(
            "events.edit_modal.people.cohosts",
          )}`}
        </Typography.LabelMd>
        <FormItem
          name="attendee_invite_attributes.invited_cohost_ids"
          className="!w-auto"
          hideLabel
        >
          <CommunityMemberPicker
            name="attendee_invite_attributes.invited_cohost_ids"
            rules={{}}
            variant={VARIANTS.BUTTON}
            countLabel={t("events.edit_modal.people.cohosts_added_label")}
            selectionLabel={t(
              "events.edit_modal.people.cohosts_selection_label",
            )}
            searchPlaceholder={t(
              "events.edit_modal.people.cohosts_placeholder",
            )}
            disableSearch={shouldDisableCommunityPickerSearch}
            excludedMemberIds={excludedMemberIds}
            modalSaveButtonLabel={t("save")}
            allowCurrentCommunityMember
            onModalSave={onAddCohosts}
            renderAfterSelection={
              isLiveLocation ? (
                <>
                  <AttendeesWarningMessage />
                  <CohostsObserver
                    setIsLimitReached={setIsCohostLimitReached}
                    initialCohostQuantity={
                      totalRecordCount + INITIAL_HOST_NUMBER
                    }
                    shouldDisplayDisabledSearchMessage={
                      shouldDisableCommunityPickerSearch
                    }
                  />
                </>
              ) : (
                <AttendeesWarningMessage />
              )
            }
          />
        </FormItem>
      </div>
      <div className="mt-4" />
      {records?.length > 0 && (
        <List event={event} records={records} onChange={refetch} />
      )}
      {totalRecordCount > 0 && (
        <Pagination
          startRecordNumber={startRecordNumber}
          endRecordNumber={endRecordNumber}
          totalRecordCount={totalRecordCount}
          fetchPrevPage={fetchPrevPage}
          fetchNextPage={fetchNextPage}
          hasPrevPage={hasPrevPage}
          hasNextPage={hasNextPage}
        />
      )}
    </div>
  );
};
